import { produce } from 'immer'
import {
  CreditCardListModel,
  CreditCardListResponse,
} from 'src/models/creditCard.models'
import { CreditCardPermissionsResponse } from 'src/models/creditCardPermissions.models'
import { UserListModel } from 'src/models/user.models'
import {
  SET_IS_REMOVE_BUDGET_USER_POPUP_OPEN,
  SetIsRemoveBudgetUserPopupOpenAction,
} from 'src/redux/actions/app.actions'
import {
  DELETE_BUDGET_USERS_SUCCESS,
  DeleteBudgetUsersSuccessAction,
  GET_CREDIT_CARD_PERMISSIONS_SUCCESS,
  GET_CREDIT_CARD_PROPERTIES_SUCCESS,
  GET_CREDIT_CARD_SUCCESS,
  GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_SUCCESS,
  GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_SUCCESS,
  GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_SUCCESS,
  GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS,
  GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS,
  GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_SUCCESS,
  GET_CREDIT_CARDS_SUCCESS,
  GET_PARENT_CREDIT_CARD_SUCCESS,
  GetCreditCardPermissionsSuccessAction,
  GetCreditCardPropertiesActions,
  GetCreditCardsStatisticsActiveBudgetCountSuccessAction,
  GetCreditCardsStatisticsActiveVcnCountSuccessAction,
  GetCreditCardsStatisticsBudgetCountSuccessAction,
  GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction,
  GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction,
  GetCreditCardsStatisticsVcnCountSuccessAction,
  GetCreditCardsSuccessAction,
  GetCreditCardSuccessAction,
  GetParentCreditCardSuccessAction,
  PATCH_CREDIT_CARD_STATUS_SUCCESS,
  PatchCreditCardStatusSuccessAction,
  POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
  POST_CREDIT_CARD_SUCCESS,
  POST_CREDIT_CARD_VERIFY_SUCCESS,
  PostCreditCardStartVerificationSuccessAction,
  PostCreditCardSuccessAction,
  PostCreditCardVerifySuccessAction,
  PUT_CREDIT_CARD_SUCCESS,
  PutCreditCardSuccessAction,
  REPLACE_CREDIT_CARD_SUCCESS,
  ReplaceCreditCardSuccessAction,
} from 'src/redux/actions/data/creditCards.actions'
import {
  GetVirtualCardActions,
  PostVirtualCardSuccessAction,
} from 'src/redux/actions/data/virtualCards.actions'

export interface CreditCardsState {
  listResponses: Record<string, CreditCardListResponse>
  permissions: Record<string, CreditCardPermissionsResponse>
  activeBudgetCount: Record<string, number>
  budgetCount: Record<string, number>
  activeVcnCount: Record<string, number>
  vcnCount: Record<string, number>
  clearedTransactionDebitAmount: Record<string, number>
  clearedTransactionCreditAmount: Record<string, number>
  budget?: CreditCardListModel
  budgetUser?: UserListModel
}

export const initialState: CreditCardsState = {
  listResponses: {},
  permissions: {},
  activeBudgetCount: {},
  budgetCount: {},
  activeVcnCount: {},
  vcnCount: {},
  clearedTransactionDebitAmount: {},
  clearedTransactionCreditAmount: {},
}

export type Actions =
  | GetCreditCardPermissionsSuccessAction
  | GetVirtualCardActions
  | PostVirtualCardSuccessAction
  | PutCreditCardSuccessAction
  | PostCreditCardSuccessAction
  | GetCreditCardSuccessAction
  | PatchCreditCardStatusSuccessAction
  | GetCreditCardsSuccessAction
  | GetCreditCardsStatisticsActiveBudgetCountSuccessAction
  | GetCreditCardsStatisticsActiveVcnCountSuccessAction
  | GetCreditCardsStatisticsBudgetCountSuccessAction
  | GetCreditCardsStatisticsVcnCountSuccessAction
  | PostCreditCardVerifySuccessAction
  | PostCreditCardStartVerificationSuccessAction
  | ReplaceCreditCardSuccessAction
  | GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction
  | GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction
  | GetParentCreditCardSuccessAction
  | DeleteBudgetUsersSuccessAction
  | SetIsRemoveBudgetUserPopupOpenAction
  | GetCreditCardPropertiesActions

const CreditCardsReducer = (
  state: CreditCardsState = initialState,
  action: Actions,
): CreditCardsState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_CREDIT_CARD_PERMISSIONS_SUCCESS: {
        draft.permissions = {
          ...draft.permissions,
          [action.payload.creditCardId]: action.payload,
        }
        return draft
      }

      case DELETE_BUDGET_USERS_SUCCESS:
      case POST_CREDIT_CARD_VERIFY_SUCCESS:
      case POST_CREDIT_CARD_START_VERIFICATION_SUCCESS:
      case PATCH_CREDIT_CARD_STATUS_SUCCESS:
      case GET_CREDIT_CARD_SUCCESS:
      case POST_CREDIT_CARD_SUCCESS:
      case PUT_CREDIT_CARD_SUCCESS:
      case GET_PARENT_CREDIT_CARD_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.creditCard.id]: action.payload.creditCard,
        }
        return draft
      }

      case GET_CREDIT_CARDS_SUCCESS: {
        draft.listResponses = action.payload.creditCards.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: curr,
          }),
          draft.listResponses,
        )
        return draft
      }

      case GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_SUCCESS: {
        draft.activeBudgetCount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.creditCardIds[index]]: curr.value,
          }),
          draft.activeBudgetCount,
        )
        return draft
      }

      case GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_SUCCESS: {
        draft.activeVcnCount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.creditCardIds[index]]: curr.value,
          }),
          draft.activeVcnCount,
        )
        return draft
      }

      case GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_SUCCESS: {
        draft.budgetCount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.creditCardIds[index]]: curr.value,
          }),
          draft.budgetCount,
        )
        return draft
      }

      case GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_SUCCESS: {
        draft.vcnCount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.creditCardIds[index]]: curr.value,
          }),
          draft.vcnCount,
        )
        return draft
      }

      case REPLACE_CREDIT_CARD_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.creditCard.id]: action.payload.creditCard,
        }
        return draft
      }

      case GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS: {
        draft.clearedTransactionDebitAmount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.creditCardIds[index]]: curr.value,
          }),
          draft.clearedTransactionDebitAmount,
        )
        return draft
      }

      case GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS: {
        draft.clearedTransactionCreditAmount = action.payload.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [action.payload.creditCardIds[index]]: curr.value,
          }),
          draft.clearedTransactionCreditAmount,
        )
        return draft
      }

      case SET_IS_REMOVE_BUDGET_USER_POPUP_OPEN: {
        draft.budget = action.payload.budget
        draft.budgetUser = action.payload.budgetUser
        return draft
      }

      case GET_CREDIT_CARD_PROPERTIES_SUCCESS: {
        const cardProperties = action.payload.cardProperties
        if (cardProperties.creditCardId !== undefined) {
          draft.listResponses[cardProperties.creditCardId].cardProperties = {
            cardHolderType: cardProperties.cardHolderType,
            cardType: cardProperties.cardType,
          }
        }
        return draft
      }
    }
  })
}

export default CreditCardsReducer
